import React, { useState, Component } from 'react';
import { FileText, Users, Image as ImageIcon, MessageSquareQuote, LayoutTemplate, Type, Bold, Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, Legend } from 'recharts';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { articles, sentimentOverTime, coverageByTier, topSources, spokespersonMentions, contentTypeLabels } from '../lib/data';
import { AntisemitismOverview } from '../components/AntisemitismOverview';
const COLORS = ['#0187c3', '#1A3A5C', '#64B5F6', '#0D5F8A', '#90CAF9', '#003F6B'];
function KPI({
  label,
  value,
  icon: Icon
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
}) {
  return <div className="bg-card border border-border rounded-lg p-6 flex flex-col gap-3" data-id="element-1752">
      <div className="flex items-center gap-2 text-sm text-muted-foreground font-normal font-heading" data-id="element-1753">
        <Icon className="h-4 w-4" data-id="element-1754" />
        {label}
      </div>
      <div className="text-3xl md:text-4xl font-light leading-none text-foreground" style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-1755">
        {value}
      </div>
    </div>;
}
function getSentimentColor(sentiment: string) {
  switch (sentiment) {
    case 'positive':
      return 'bg-[#0187c3]';
    case 'neutral':
      return 'bg-[#64B5F6]';
    case 'negative':
      return 'bg-[#1A3A5C]';
    default:
      return 'bg-muted';
  }
}
export function Statistics() {
  const [activeTab, setActiveTab] = useState('panoramica');
  const [startDate, setStartDate] = useState('2025-04-01');
  const [endDate, setEndDate] = useState('2025-04-15');
  // Calculate KPIs
  const frontPageCount = articles.filter(a => a.frontPageCallout).length;
  const uniqueAuthors = new Set(articles.map(a => a.author)).size;
  const withPhotoCount = articles.filter(a => a.hasPhoto).length;
  const titleMentionCount = articles.filter(a => a.titleMentionsClient).length;
  const boldReferenceCount = articles.filter(a => a.boldedReference).length;
  // Calculate Content Type Distribution
  const contentTypeCounts = articles.reduce((acc, article) => {
    acc[article.contentType] = (acc[article.contentType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const contentTypeData = Object.entries(contentTypeCounts).map(([type, count]) => ({
    type,
    label: contentTypeLabels[type as keyof typeof contentTypeLabels],
    count,
    percentage: count / articles.length * 100
  })).sort((a, b) => b.count - a.count);
  // Calculate Top Authors
  const authorStats = articles.reduce((acc, article) => {
    if (!acc[article.author]) {
      acc[article.author] = {
        count: 0,
        totalScore: 0
      };
    }
    acc[article.author].count += 1;
    acc[article.author].totalScore += article.relevanceScore;
    return acc;
  }, {} as Record<string, {
    count: number;
    totalScore: number;
  }>);
  const topAuthorsList = Object.entries(authorStats).map(([name, stats]) => ({
    name,
    count: stats.count,
    avgScore: stats.totalScore / stats.count
  })).sort((a, b) => b.count - a.count).slice(0, 5);
  // Calculate Sections
  const sectionCounts = articles.reduce((acc, a) => {
    acc[a.section] = (acc[a.section] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const sectionData = Object.entries(sectionCounts).map(([name, count]) => ({
    name,
    count,
    percentage: count / articles.length * 100
  })).sort((a, b) => b.count - a.count);
  // Calculate Geo Distribution (Mock mapping)
  const getRegion = (source: string) => {
    const s = source.toLowerCase();
    if (s.includes('messaggero')) return 'Lazio';
    if (s.includes('mattino')) return 'Campania';
    if (s.includes('gazzettino')) return 'Veneto';
    if (s.includes('nazione')) return 'Toscana';
    if (s.includes('secolo')) return 'Liguria';
    return 'Nazionale';
  };
  const geoCounts = articles.reduce((acc, a) => {
    const reg = getRegion(a.source);
    acc[reg] = (acc[reg] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const geoData = Object.entries(geoCounts).map(([name, count]) => ({
    name,
    count,
    percentage: count / articles.length * 100
  })).sort((a, b) => b.count - a.count);
  const maxTopSourceArticles = Math.max(...topSources.map(s => s.articles));
  // Reusable Components for duplicated sections across tabs
  const SentimentChart = () => <div className="bg-card border border-border rounded-lg p-6" data-id="element-1756">
      <h3 className="text-lg font-medium mb-6" data-id="element-1757">Sentiment nel tempo</h3>
      <div className="h-64" data-id="element-1758">
        <ResponsiveContainer width="100%" height="100%" data-id="element-1759">
          <AreaChart data={sentimentOverTime} margin={{
          top: 5,
          right: 0,
          bottom: 0,
          left: -20
        }} data-id="element-1760">
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-1761" />
            <XAxis dataKey="date" tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} dy={10} data-id="element-1762" />
            <YAxis tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-1763" />
            <Tooltip data-id="element-1764" />
            <Area type="monotone" dataKey="positive" stackId="1" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1765" />
            <Area type="monotone" dataKey="neutral" stackId="1" stroke="#64B5F6" fill="#64B5F6" fillOpacity={0.35} strokeWidth={1.5} data-id="element-1766" />
            <Area type="monotone" dataKey="negative" stackId="1" stroke="#1A3A5C" fill="#1A3A5C" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1767" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>;
  const SectionsChart = () => <div className="bg-card border border-border rounded-lg p-6" data-id="element-1768">
      <h3 className="text-lg font-medium mb-6" data-id="element-1769">Distribuzione per sezione</h3>
      <div className="h-72" data-id="element-1770">
        <ResponsiveContainer width="100%" height="100%" data-id="element-1771">
          <BarChart data={sectionData} margin={{
          top: 5,
          right: 0,
          left: -20,
          bottom: 5
        }} data-id="element-1772">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E5" data-id="element-1773" />
            <XAxis dataKey="name" tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} dy={10} data-id="element-1774" />
            <YAxis tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-1775" />
            <Tooltip cursor={{
            fill: 'rgba(0,0,0,0.05)'
          }} data-id="element-1776" />
            <Bar dataKey="count" fill="#0187c3" radius={[4, 4, 0, 0]} barSize={40} data-id="element-1777" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>;
  const TopAuthorsList = () => <div className="bg-card border border-border rounded-lg p-6" data-id="element-1778">
      <h3 className="text-lg font-medium mb-6" data-id="element-1779">Top autori</h3>
      <div className="divide-y divide-border" data-id="element-1780">
        {topAuthorsList.map(author => <div key={author.name} className="flex items-center justify-between py-3 first:pt-0 last:pb-0" data-id="element-1781">
            <div className="text-[15px] font-normal text-foreground truncate pr-4 font-heading" data-id="element-1782">
              {author.name}
            </div>
            <div className="flex items-center gap-6 flex-shrink-0 text-right" data-id="element-1783">
              <div data-id="element-1784">
                <div className="font-light text-base" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1785">
                  {author.count}
                </div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider font-normal" data-id="element-1786">
                  Articoli
                </div>
              </div>
              <div data-id="element-1787">
                <div className="font-light text-base text-accent" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1788">
                  {author.avgScore.toFixed(1)}
                </div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider font-normal" data-id="element-1789">
                  Score
                </div>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
  return <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col gap-[0px] w-full" data-id="element-1790">
      {/* Header */}
      <div className="pt-6 md:pt-8 px-6 max-w-[1400px] mx-auto w-full bg-[#FFFFFF]" data-id="element-1791">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-1792">
          <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-1793">
            Statistiche
          </h1>
          <div className="flex items-center gap-2 text-sm" data-id="element-1794">
            <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" data-id="element-1795" />
            <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2" data-id="element-1796">
              <label className="text-muted-foreground text-xs font-medium whitespace-nowrap" data-id="element-1797">
                Da
              </label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent border-none outline-none text-sm text-foreground w-[130px] cursor-pointer" data-id="element-1798" />
              <span className="text-muted-foreground mx-1" data-id="element-1799">—</span>
              <label className="text-muted-foreground text-xs font-medium whitespace-nowrap" data-id="element-1800">
                A
              </label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent border-none outline-none text-sm text-foreground w-[130px] cursor-pointer" data-id="element-1801" />
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar - sticky */}
      <div className="border-b border-border sticky top-0 z-10 bg-background" data-id="element-1802">
        <div className="px-6 max-w-[1400px] mx-auto w-full flex items-end gap-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] bg-[#FFFFFF]" data-id="element-1803">
          {[{
          id: 'panoramica',
          label: 'Panoramica'
        }, {
          id: 'contenuto',
          label: 'Contenuto'
        }, {
          id: 'posizionamento',
          label: 'Posizionamento'
        }, {
          id: 'fonti',
          label: 'Fonti & Autori'
        }, {
          id: 'sentiment',
          label: 'Sentiment'
        }, {
          id: 'antisemitismo',
          label: 'Antisemitismo'
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="relative px-5 py-3 text-sm transition-colors whitespace-nowrap font-heading" style={{
          backgroundColor: activeTab === tab.id ? '#FFFFFF' : 'transparent',
          color: activeTab === tab.id ? 'var(--foreground)' : 'var(--muted-foreground)',
          fontWeight: activeTab === tab.id ? 500 : 400
        }} data-id="element-1804">
              {tab.label}
              {activeTab === tab.id && <span className="absolute bottom-0 left-0 right-0" style={{
            height: '2px',
            backgroundColor: '#0187c3'
          }} data-id="element-1805" />}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8 max-w-[1400px] mx-auto w-full" data-id="element-1806">
        <TabsContent value="panoramica" className="m-0 space-y-8" data-id="element-1807">
          {/* KPI Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1808">
            <KPI label="Prime pagine" value={frontPageCount} icon={FileText} data-id="element-1809" />
            <KPI label="Autori unici" value={uniqueAuthors} icon={Users} data-id="element-1810" />
            <KPI label="Con foto" value={withPhotoCount} icon={ImageIcon} data-id="element-1811" />
            <KPI label="Titolo con menzione" value={titleMentionCount} icon={MessageSquareQuote} data-id="element-1812" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1813">
            <SentimentChart data-id="element-1814" />

            {/* Coverage by Tier (Radar) */}
            <div className="bg-card border border-border rounded-lg p-6" data-id="element-1815">
              <h3 className="text-lg font-medium mb-6" data-id="element-1816">Copertura per tier</h3>
              <div className="h-64" data-id="element-1817">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1818">
                  <RadarChart cx="50%" cy="50%" outerRadius="70%" data={coverageByTier} data-id="element-1819">
                    <PolarGrid stroke="#E8E8E5" data-id="element-1820" />
                    <PolarAngleAxis dataKey="tier" tick={{
                    fontSize: 12,
                    fill: '#8B8680'
                  }} data-id="element-1821" />
                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={false} axisLine={false} data-id="element-1822" />
                    <Radar name="Copertura" dataKey="percentage" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} data-id="element-1823" />
                    <Tooltip data-id="element-1824" />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contenuto" className="m-0 space-y-8" data-id="element-1825">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1826">
            {/* Content Type Distribution (Donut) */}
            <div className="bg-card border border-border rounded-lg p-6" data-id="element-1827">
              <h3 className="text-lg font-medium mb-6" data-id="element-1828">
                Distribuzione per tipologia
              </h3>
              <div className="h-72" data-id="element-1829">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1830">
                  <PieChart data-id="element-1831">
                    <Pie data={contentTypeData} innerRadius={70} outerRadius={100} paddingAngle={2} dataKey="count" nameKey="label" data-id="element-1832">
                      {contentTypeData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-1833" />)}
                    </Pie>
                    <Tooltip data-id="element-1834" />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{
                    fontSize: '12px'
                  }} data-id="element-1835" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <SectionsChart data-id="element-1836" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1837">
            <TopAuthorsList data-id="element-1838" />
          </div>
        </TabsContent>

        <TabsContent value="posizionamento" className="m-0 space-y-8" data-id="element-1839">
          {/* Article Positioning */}
          <div className="bg-card border border-border rounded-lg p-6" data-id="element-1840">
            <h3 className="text-lg font-medium mb-6" data-id="element-1841">
              Posizionamento articoli
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1842">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1843">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1844">
                  <LayoutTemplate className="h-5 w-5 text-muted-foreground" data-id="element-1845" />
                </div>
                <div data-id="element-1846">
                  <div className="text-2xl font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1847">
                    {frontPageCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5 font-normal" data-id="element-1848">
                    Prima pagina
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1849">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1850">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" data-id="element-1851" />
                </div>
                <div data-id="element-1852">
                  <div className="text-2xl font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1853">
                    {withPhotoCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5 font-normal" data-id="element-1854">
                    Con foto
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1855">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1856">
                  <Bold className="h-5 w-5 text-muted-foreground" data-id="element-1857" />
                </div>
                <div data-id="element-1858">
                  <div className="text-2xl font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1859">
                    {boldReferenceCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5 font-normal" data-id="element-1860">
                    Rif. in grassetto
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1861">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1862">
                  <Type className="h-5 w-5 text-muted-foreground" data-id="element-1863" />
                </div>
                <div data-id="element-1864">
                  <div className="text-2xl font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1865">
                    {titleMentionCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5 font-normal" data-id="element-1866">
                    Menzione titolo
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1867">
            <SectionsChart data-id="element-1868" />
          </div>
        </TabsContent>

        <TabsContent value="fonti" className="m-0 space-y-8" data-id="element-1869">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1870">
            {/* Top Sources (Horizontal Bar) */}
            <div className="bg-card border border-border rounded-lg p-6" data-id="element-1871">
              <h3 className="text-lg font-medium mb-6" data-id="element-1872">Top testate</h3>
              <div className="h-80" data-id="element-1873">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1874">
                  <BarChart layout="vertical" data={topSources} margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }} data-id="element-1875">
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E8E8E5" data-id="element-1876" />
                    <XAxis type="number" hide data-id="element-1877" />
                    <YAxis dataKey="name" type="category" width={100} tick={{
                    fontSize: 12,
                    fill: '#8B8680'
                  }} axisLine={false} tickLine={false} data-id="element-1878" />
                    <Tooltip cursor={{
                    fill: 'rgba(0,0,0,0.05)'
                  }} data-id="element-1879" />
                    <Bar dataKey="articles" fill="#0187c3" radius={[0, 4, 4, 0]} barSize={20} name="Articoli" data-id="element-1880" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <TopAuthorsList data-id="element-1881" />

            {/* Geo Distribution */}
            <div className="bg-card border border-border rounded-lg p-6 lg:col-span-2" data-id="element-1882">
              <h3 className="text-lg font-medium mb-6" data-id="element-1883">
                Distribuzione geografica
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5" data-id="element-1884">
                {geoData.map(region => <div key={region.name} data-id="element-1885">
                    <div className="flex items-center justify-between mb-2 text-[15px]" data-id="element-1886">
                      <span className="text-foreground font-normal" data-id="element-1887">
                        {region.name}
                      </span>
                      <span className="text-muted-foreground font-normal" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1888">
                        {region.count} art. ({region.percentage.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-1889">
                      <div className="h-full bg-accent rounded-full transition-all" style={{
                    width: `${region.percentage}%`
                  }} data-id="element-1890" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sentiment" className="m-0 space-y-8" data-id="element-1891">
          <SentimentChart data-id="element-1892" />

          {/* Spokespersons */}
          <div className="bg-card border border-border rounded-lg p-6" data-id="element-1893">
            <h3 className="text-lg font-medium mb-6" data-id="element-1894">Portavoce citati</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1895">
              {spokespersonMentions.map(person => <div key={person.name} className="flex items-center justify-between p-4 border border-border rounded-md bg-muted/10" data-id="element-1896">
                  <div className="min-w-0 pr-4" data-id="element-1897">
                    <div className="text-[15px] font-normal text-foreground truncate font-heading" data-id="element-1898">
                      {person.name}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5 font-normal" data-id="element-1899">
                      {person.role}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1900">
                    <span className="font-light text-lg" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1901">
                      {person.mentions}
                    </span>
                    <div className={`w-2.5 h-2.5 rounded-full ${getSentimentColor(person.avgSentiment)}`} data-id="element-1902" />
                  </div>
                </div>)}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="antisemitismo" className="m-0 space-y-8" data-id="element-1903">
          <AntisemitismOverview articles={articles} heading="Antisemitismo rilevato" data-id="element-1904" />
        </TabsContent>
      </div>
    </Tabs>;
}